/**
 * What the Configure app search needs to know about each SDL template: the
 * words it is found by and the name it shows. Nothing else.
 *
 * Kept apart from `templates.ts` on purpose. The templates themselves render
 * through the `yaml` package and belong to the SDL editor's lazy chunk; the
 * app search sits on the first screen a tenant sees. `templates.test.ts`
 * asserts that this list and `TEMPLATES` describe the same templates, field
 * for field, so the two cannot drift.
 */

import { CATEGORY_LABELS, POPULAR_IMAGES, normaliseQuery, presetSearchFields, searchPopularImages, searchRank, type PopularImage, type SearchFields } from './popularImages'

export const TEMPLATE_CATEGORIES = ['web', 'database', 'cache-queue', 'ai'] as const
export type TemplateCategory = (typeof TEMPLATE_CATEGORIES)[number]

export const TEMPLATE_CATEGORY_LABELS: Record<TemplateCategory, string> = {
  web: CATEGORY_LABELS.web,
  database: CATEGORY_LABELS.database,
  'cache-queue': CATEGORY_LABELS['cache-queue'],
  ai: 'AI',
}

/**
 * The page an instance created from this template opens on (the App tab).
 * Here rather than in `templates.ts` so the instance pages never load the
 * SDL renderer to show a name and a link.
 */
export interface AppDescriptor {
  /** The service whose public URI is "Open <app>". */
  primaryService: string
  openLabel: string
  /** Non-secret params only. */
  facts: Array<{ label: string; param: string }>
  notes: string[]
}

export interface TemplateSummary {
  /** Stable, kebab-case. Also the `?template=` value in the SDL editor's URL. */
  id: string
  name: string
  description: string
  category: TemplateCategory
  /** Search aliases, lower case. */
  keywords: string[]
  /** A file under `public/logos/`, or null for the template glyph. */
  logo: string | null
  /**
   * Every public port served without TLS, so the tile says so before the
   * template is chosen, whether or not a password guards it (no template
   * terminates TLS yet). `templates.test.ts` derives it from the exposure.
   */
  unencryptedPorts?: readonly number[]
  /**
   * The params whose answers may be kept with a deployment: every one that is
   * not a secret. `templates.test.ts` checks it against the template's params.
   */
  answerKeys: readonly string[]
  app?: AppDescriptor
}

export const TEMPLATE_SUMMARIES: readonly TemplateSummary[] = [
  {
    id: 'web',
    name: 'Single web service',
    description: 'One container, one port, exposed globally. The smallest thing that runs.',
    category: 'web',
    keywords: ['nginx', 'web server', 'http', 'static site', 'hello world'],
    logo: 'nginx.svg',
    unencryptedPorts: [80],
    answerKeys: [],
  },
  {
    id: 'replicated',
    name: 'Replicated service',
    description: 'Three replicas, each its own lease — the shape HA workloads start from.',
    category: 'web',
    keywords: ['replicas', 'high availability', 'ha', 'nginx', 'scale'],
    logo: 'nginx.svg',
    unencryptedPorts: [80],
    answerKeys: [],
  },
  {
    id: 'persistent',
    name: 'PostgreSQL with a volume',
    description: 'PostgreSQL on a persistent volume, so the data outlives a restart.',
    category: 'database',
    keywords: ['postgres', 'postgresql', 'sql', 'volume', 'persistent', 'database'],
    logo: 'postgresql.svg',
    unencryptedPorts: [5432],
    answerKeys: [],
  },
  {
    id: 'redis-password',
    name: 'Redis with a password',
    description: 'Redis that refuses every command until the client sends your password.',
    category: 'cache-queue',
    keywords: ['redis', 'cache', 'key value', 'queue', 'requirepass'],
    logo: null,
    unencryptedPorts: [6379],
    answerKeys: [],
  },
  {
    id: 'personal-llm',
    name: 'Personal LLM',
    description: 'A private chat assistant: Ollama behind Open WebUI, with your own admin login.',
    category: 'ai',
    keywords: ['llm', 'ai', 'chat', 'chatbot', 'ollama', 'gpt', 'assistant', 'open webui'],
    logo: null,
    unencryptedPorts: [80],
    answerKeys: ['MODEL', 'ADMIN_EMAIL'],
    app: {
      primaryService: 'webui',
      openLabel: 'Open chat',
      facts: [
        { label: 'Model', param: 'MODEL' },
        { label: 'Admin email', param: 'ADMIN_EMAIL' },
      ],
      notes: [
        // Said as it is: deployedSdl.ts keeps the signed SDL, env and all, so
        // Start workload can resend it. Only the details below leave it out.
        "Sign in with the admin email and password you set. The password is part of this deployment's configuration, which this browser keeps and the host can read; it is not kept with the details above.",
        'The first start downloads the model, which can take several minutes.',
        'Chats are kept on the instance disk, so a restart loses them.',
      ],
    },
  },
  {
    id: 'static-caddy',
    name: 'Caddy static site',
    description: 'Caddy serving a static page. Build your own site on the same image.',
    category: 'web',
    keywords: ['caddy', 'web server', 'static site', 'http'],
    logo: 'caddy.svg',
    unencryptedPorts: [80],
    answerKeys: [],
  },
]

export function templateSummary(id: string): TemplateSummary | undefined {
  return TEMPLATE_SUMMARIES.find((template) => template.id === id)
}

/** Where a template opens. The only way into the SDL editor on one. */
export function templateEditorPath(id: string): string {
  return `/app/advanced/sdl?template=${encodeURIComponent(id)}`
}

export function templateSearchFields(template: TemplateSummary): SearchFields {
  return { name: template.name, keywords: template.keywords, secondary: [TEMPLATE_CATEGORY_LABELS[template.category], template.category] }
}

/** The template gallery's search. Empty: every template, in list order. */
export function searchTemplates<T extends TemplateSummary>(query: string, list: readonly T[]): T[] {
  const q = normaliseQuery(query)
  if (q === '') return [...list]
  return list
    .flatMap((template) => {
      const rank = searchRank(q, templateSearchFields(template))
      return rank === null ? [] : [{ template, rank }]
    })
    .sort((a, b) => a.rank - b.rank || a.template.name.localeCompare(b.template.name))
    .map(({ template }) => template)
}

export type AppResult = { kind: 'image'; preset: PopularImage } | { kind: 'template'; template: TemplateSummary }

export function appResultName(result: AppResult): string {
  return result.kind === 'image' ? result.preset.name : result.template.name
}

/**
 * The Configure app search: images and templates, ranked together by the
 * same rules. An empty query is "Popular", which is images only — a template
 * opens a different screen, so it is found by asking for it, never offered.
 * A template is never featured, so it follows an image of the same rank.
 */
export function searchApps(query: string, images: readonly PopularImage[] = POPULAR_IMAGES, templates: readonly TemplateSummary[] = TEMPLATE_SUMMARIES): AppResult[] {
  const q = normaliseQuery(query)
  if (q === '') return searchPopularImages('', images).map((preset) => ({ kind: 'image', preset }))
  const ranked: Array<{ result: AppResult; rank: number; featured: boolean }> = []
  for (const preset of images) {
    const rank = searchRank(q, presetSearchFields(preset))
    if (rank !== null) ranked.push({ result: { kind: 'image', preset }, rank, featured: preset.featured })
  }
  for (const template of templates) {
    const rank = searchRank(q, templateSearchFields(template))
    if (rank !== null) ranked.push({ result: { kind: 'template', template }, rank, featured: false })
  }
  return ranked
    .sort((a, b) => a.rank - b.rank || Number(b.featured) - Number(a.featured) || appResultName(a.result).localeCompare(appResultName(b.result)))
    .map(({ result }) => result)
}
